import React from 'react';
import { Layout } from '@/components/Layout';
import { Col, Container } from '@visalex/core';

import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';

import styles from '@/components/Terms/Terms.module.scss';

const Terms = () => {
  const { t } = useTranslation();

  return (
    <Layout title={t('terms.seo-title')} description={t('terms.seo-description')}>
      <Container className={styles.terms}>
        <Col>
          <h1>
            THIS IS A CONTRACT BETWEEN THE ENTITY NAMED IN THE APPLICABLE CUSTOMER FIELD HEREIN (“YOU” or “YOUR”), AND
            VISALEX, LLC (“VISALEX”), A DELAWARE LIMITED LIABILITY COMPANY (the “AGREEMENT”). THIS AGREEMENT CONTAINS AN
            ARBITRATION PROVISION AND LIMITATIONS ON VISALEX’S LIABILITY TO YOU. PLEASE READ IT CAREFULLY. BY CLICKING
            “I AGREE” BELOW AND CONTINUING TO USE THE VISALEX SITE, YOU REPRESENT AND WARRANT THAT YOU HAVE THE FULL
            AUTHORITY TO ACCEPT THIS AGREEMENT ON YOUR BEHALF AND DO SO INTENDING THAT YOU WILL BE BOUND THEREBY. ONCE
            YOU OPEN AN ACCOUNT WITH VISALEX YOU ARE AGREEING TO THESE TERMS FOR EVERY CASE YOU OPEN UNDER THIS ACCOUNT.
          </h1>
          <h2>1. GENERAL SERVICE TERMS</h2>
          <p>
            1.1. VISALEX is a provider of technology, tools, and administrative support (“Support Services”) for its
            customers’ immigration matters (“Matters”). Any legal services provided in conjunction with Your Matters
            will be provided by the attorneys at American Immigration Associates, LLC (“AIA LLC”), an Illinois limited
            liability company, or other attorneys selected by VISALEX to provide legal services (collectively,
            “Attorneys”) in connection with the preparation and submission of visa applications for nonimmigrant and
            immigrant visas, permanent residence, and citizenship status to the U.S. Citizenship and Immigration
            Services and other governmental agencies regulating foreign persons entering the United States.
          </p>
          <p>
            1.2. In order for VISALEX and the Attorneys to prepare and complete Your Matters effectively, You must
            provide VISALEX with all necessary documentation, information, and other required materials in a timely
            fashion. You must also otherwise cooperate with VISALEX and the Attorneys, with respect to Your Matters as
            either may reasonably request from time to time. Failure to provide such cooperation may result in
            termination of this Agreement.
          </p>
          <p>
            1.3. Information provided by You to VISALEX will be shared by VISALEX with, where applicable, the Attorneys,
            Your current or prospective employees, and those government agencies as the filing and processing of Your
            Matters may require. Your information will be treated in accordance with this Agreement and the VISALEX
            privacy policy (visalex.com/privacy-policy).
          </p>
          <p>
            1.4. You may request the return of printed documents and tangible physical materials provided by You to
            VISALEX or copies of government filings made on Your behalf (to the extent copies of such filings are in the
            possession of VISALEX), in conjunction with a termination of this Agreement. VISALEX will return the
            relevant tangible physical materials and/or filing copies in its possession to You within a reasonable time
            after receipt by VISALEX of Your written request. With respect to information provided electronically by You
            or filed electronically on Your behalf, You may also make a written request for the deletion of any such
            information that is in the possession of VISALEX, subject to the then-current VISALEX data backup and
            retention policy, and applicable law.
          </p>

          <h2>2. SERVICE REPRESENTATIONS</h2>
          <p>
            2.1. VISALEX represents and warrants that (a) VISALEX has the capability and experience, as well as all
            applicable title, registrations, licenses, permits, and governmental approvals, if any, necessary to perform
            the Support Services, (b) the Support Services will be performed to the best of VISALEX’s ability, which
            shall be no less than a good and workmanlike manner, (c) the Support Services and any and all advice,
            technology and other products of the Support Services shall comply with all applicable laws and regulations,
            (d) technology and tools do not infringe upon the intellectual property rights of any third party, and (e)
            VISALEX will devote sufficient resources to ensure that the Support Services are performed in a timely and
            reliable manner.
          </p>
          <p>
            2.2. “Confidential Information” means (a) any proprietary information that is disclosed to VISALEX under
            this Agreement, including but not limited to Your actual or anticipated business plans, technical data,
            trade secrets and know-how, and (b) information provided to VISALEX by or at the direction of You or Your
            employees that identifies or can be used to identify or authenticate an individual. and (c) any privileged
            and confidential communications facilitated by VISALEX for You and the Attorneys. Confidential Information
            does not include information that has become publicly known and made generally available through no wrongful
            act of VISALEX or that VISALEX can show was in its possession without any obligation of confidentiality
            prior to its receipt of that information.
          </p>
          <p>
            2.3. VISALEX will not, during or subsequent to the term of this Agreement, (a) use Confidential Information
            for any purpose whatsoever other than the performance of services on Your behalf or (b) disclose
            Confidential Information to anyone other than Authorized Parties. “Authorized Parties” means (i) VISALEX’s
            employees and subcontractors who have a need to know such Confidential Information to enable VISALEX to
            perform its obligations under this Agreement, (ii) the Attorneys, (iii) other agents engaged by VISALEX to
            assist with the Matters and (iv) third parties, including government agencies, to whom you have authorized
            VISALEX or AIA to make disclosures for purposes of pursuing your immigration and related goals.
          </p>

          <h2>3. FEES AND PAYMENT</h2>
          <p>
            3.1. Fees for the Support Services and technology platform furnished to You by VISALEX, and for the legal
            representation to be furnished by the Attorneys (collectively, “Service Fees”) and charges for all expenses
            associated with pursuing the application, including without limitation applicable government form and filing
            fees, and expenses for related third-party services, will be assessed as set forth in the price quotation
            generated for You by VISALEX in conjunction with Your acceptance of this Agreement, or, as the case may be,
            following your acceptance of this Agreement, during the pendency of your Matter. You agree that the charges
            set forth in that quotation will be invoiced to You (or, if You have a credit card on file with VISALEX,
            charged to the credit card on Your file) upon Your acknowledgment of acceptance of these Terms and
            Conditions, or during the pendency of your Matter, as the case may be. Any fees (including, without
            limitation, ServiceFees) which are not paid in advance will be due and payable by You upon receipt of
            VISALEX’s invoice to You. In the event that any undisputed amounts due under this Agreement are not paid
            upon receipt of VISALEX’s invoice to You, VISALEX may set off against, deduct from and recoup any such
            overdue amounts from Credits (defined below) on Your account, and reserves the right to terminate this
            Agreement, and cease providing any further services under this Agreement. VISALEX fees do not cover consular
            application fees, medical exam fees, and any other fees associated with obtaining the required documentation
            for Your case (e.g., translation fees, advertising expenses, additional fees for overnight courier, etc.).
            Where necessary to pursue the Matter, such additional fees will be charged to You. You acknowledge that
            amounts paid by You toward government filing fees will not be held by VISALEX in segregated accounts prior
            to VISALEX’s payment of those amounts to the appropriate governmental agency.
          </p>
          <p>
            3.2. If governmental agencies increase filing fees or impose additional costs for filing of Your
            application(s), these costs will be added to the original Service Fees. Additional amounts arising under
            this Section, if any, will be invoiced to You as set forth above (or, if you have a credit card on file with
            VISALEX, charged to Your credit card at the time of filing or invoiced to You), depending on the payment
            terms agreed by VISALEX for Your Matter.
          </p>
          <p>
            3.3. In the event of termination of the Matter by You or VISALEX prior to completion of all work related to
            your Matter, the percentage of fees that will be owed by You or retained by VISALEX if prepaid by You, will
            depend on the status of Your case and shall be calculated as follows:
          </p>
          <table cellSpacing="0">
            <thead>
              <tr>
                <th>Case Status</th>
                <th>Refund %</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Terminated within 24 hours of initiation</td>
                <td>100% of service and filing fees</td>
              </tr>
              <tr>
                <td>Terminated after 24 hours of initiation but before submission to Government</td>
                <td>20% of service fees; 100 percent of filing fees</td>
              </tr>
              <tr>
                <td>Terminated after 6 months of initiation but before submission to Government</td>
                <td>No refund of service fees; 100 percent of filing fees</td>
              </tr>
              <tr>
                <td>Terminated after submission to Government</td>
                <td>No refund</td>
              </tr>
            </tbody>
          </table>
          <p>
            Any filing fees to be refunded include only such fees as were paid to VISALEX but not yet disbursed to the
            government. You shall not be entitled to the refund of any fees already disbursed to the government.
          </p>
          <p>
            Further, notwithstanding the above, in the event that, within one calendar year of You making the first
            payment of any Service Fees on Your Matter, You fail to provide VISALEX with any or all necessary
            documentation, information, and/or other materials relevant to Your Matter, VISALEX shall: (a) terminate
            Your Matter without further notice; (b) close your case in the VISALEX platform without further notice; and
            (c) refund to You any filing fees paid to VISALEX but not yet disbursed to the government. In such event,
            You shall not be entitled to, and shall not receive, the refund of any Service Fees already paid to VISALEX.
          </p>

          <h2>4. DISPUTE RESOLUTION</h2>
          <p>
            4.1. If You and VISALEX are unable to resolve any dispute or claim arising between You and VISALEX pursuant
            to this Agreement through informal discussions, You and VISALEX agree to submit such disputes to binding
            arbitration or, if possible, small claims court, rather than to a court of general jurisdiction. Any
            arbitration under this Section will take place on an individual basis; class arbitrations and class actions
            are not permitted. You agree that, by entering into this Agreement, You and VISALEX are each waiving the
            right to a trial by jury or to participate in a class action. You and VISALEX agree that this arbitration
            agreement is governed by the Federal Arbitration Act (“FAA”).
          </p>
          <p>
            4.2. For the purpose of this Agreement, “dispute or claim” means any assertion of a right, dispute or
            controversy between You and VISALEX arising from or relating to this Agreement and/or the relationships
            resulting from this Agreement. This includes claims of every kind and nature including, but not limited to,
            initial claims, counterclaims, cross-claims, third-party claims, and claims based upon contract, tort,
            intentional tort, statutes, regulations, common law and equity. VISALEX will not compel arbitration under
            this Agreement for any individual claim that You properly file and pursue in a small claims court of
            competent jurisdiction, so long as Your claim is pending only in that court.
          </p>
          <p>
            4.3. A party intending to seek arbitration must, before commencing arbitration, send a written Notice of
            Dispute (“Notice”) to the other party via U.S. certified mail, return receipt requested. A Notice to VISALEX
            should be addressed to: VISALEX, LLC, Notice of Dispute, 300 S. Orange Ave #1150, Orlando, FL 32801, ATTN:
            Chief Legal Officer. The Notice must (a) describe the nature and basis of the claim or dispute and (b) set
            forth the specific relief sought (“Demand”). If VISALEX and You do not reach an agreement to resolve the
            claim within 30 days of its receipt, either You or VISALEX may commence an arbitration proceeding.
          </p>
          <p>
            4.4. The arbitration will be governed by the Commercial Arbitration Rules (the “AAA Rules”) of the American
            Arbitration Association (the “AAA”), as modified by this Agreement, and will be administered by the AAA. The
            arbitrator is bound by this Agreement. All issues are for the arbitrator to decide, except issues relating
            to the scope, enforceability, and interpretation of the arbitration provision. The parties agree that in any
            arbitration of a dispute or claim, neither party will rely for preclusive effect on any award or finding of
            fact nor conclusion of law made in any other arbitration of any dispute or claim to which VISALEX was a
            party.
          </p>
          <p>
            4.5. The arbitrator may award injunctive relief only in favor of the individual party seeking relief and
            only to the extent necessary to provide relief warranted by that party’s individual claim. Further, unless
            both You and VISALEX agree otherwise, the arbitrator may not consolidate more than one person’s claims, and
            may not otherwise preside over any form of a representative or class proceeding. The arbitrator may award
            any relief that a court could award that is individualized to the claimant and would not affect other
            customers. Neither You nor VISALEX may seek non-individualized relief that would affect other customers. If
            a court decides that applicable law precludes enforcement of any of this paragraph’s limitation as to a
            particular claim for relief, then that claim (and only that claim) must be severed from the arbitration and
            may be brought in court.
          </p>

          <h2>5. DISCLAIMERS AND LIMITATIONS OF LIABILITY</h2>
          <p>
            5.1. VISALEX MAKES NO WARRANTY, EXPRESS OR IMPLIED, THAT: (A) THE WEBSITE AT VISALEX.COM OR
            CLIENT.VISALEX.COM (“SITES”), THE FUNCTIONALITY CONTAINED THEREIN, OR ANY OTHER APPLICATIONS OR MATERIALS
            FURNISHED BY VISALEX, WILL BE AVAILABLE ON AN UNINTERRUPTED, TIMELY, SECURE OR ERROR-FREE BASIS; (B) THE
            RESULTS THAT MAY BE OBTAINED FROM THE USE OF THE SITES, APPLICATIONS, OR MATERIALS FURNISHED BY VISALEX WILL
            BE ACCURATE; OR (C) THE QUALITY OF ANY SERVICES, INFORMATION OR OTHER MATERIAL PURCHASED OR OBTAINED BY YOU
            THROUGH THE SITES OR THEIR ASSOCIATED FUNCTIONALITY WILL MEET YOUR REQUIREMENTS. ALL MATERIALS, INFORMATION,
            DOCUMENTS OR FORMS PROVIDED ON OR THROUGH YOUR USE OF THE SITES OR OTHERWISE FURNISHED TO YOU BY VISALEX ARE
            PROVIDED ON AN “AS IS” AND “AS AVAILABLE” BASIS. TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW, VISALEX
            EXPRESSLY DISCLAIMS ALL WARRANTIES OF ANY KIND, WHETHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO
            IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND NONINFRINGEMENT.
          </p>
          <p>
            5.2. ACCESS TO THE SITES AND USE OF THE APPLICATIONS, MATERIALS AND FUNCTIONALITY CONTAINED THEREIN IS AT
            YOUR OWN RISK. IN NO EVENT WILL VISALEX BE LIABLE TO YOU OR TO ANY THIRD PARTY FOR ANY DAMAGE TO COMPUTER
            SYSTEMS, LOSS OF DATA, OR INTERFERENCE WITH THE OPERATION OF ANY SYSTEM OR NETWORKS THAT RESULTS FROM YOUR
            ACCESS OF THE SITES, OR YOUR USE OR DOWNLOAD OF ANY CONTENT, MATERIALS, INFORMATION OR FUNCTIONALITY
            CONTAINED THEREIN.
          </p>
          <p>
            5.3. EXCEPT TO THE EXTENT PROHIBITED BY APPLICABLE LAW, YOU AGREE TO HOLD VISALEX, TOGETHER WITH ITS
            OFFICERS, DIRECTORS, EMPLOYEES, AFFILIATES AND AGENTS (“THE VISALEX PARTIES”), HARMLESS FROM AND AGAINST ANY
            INDIRECT, PUNITIVE, SPECIAL, INCIDENTAL, OR CONSEQUENTIAL DAMAGE, REGARDLESS OF THE FORM OF CLAIM, DEMAND,
            OR ACTION FOR SUCH DAMAGES, ARISING OUT OF OR RELATING TO THE SUPPORT SERVICES OR THIS AGREEMENT, INCLUDING
            WITHOUT LIMITATION ANY CLAIM FOR PERSONAL INJURY OR PROPERTY DAMAGE,EVEN IF VISALEX HAS BEEN PREVIOUSLY
            ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
          </p>
          <p>
            5.4. ADDITIONALLY, YOU AGREE TO HOLD THE VISALEX PARTIES HARMLESS FROM AND AGAINST ANY AND ALL THIRD-PARTY
            CLAIMS, LOSSES, AND DAMAGES (INCLUDING WITHOUT LIMITATION REASONABLE ATTORNEY’S FEES AND COURT COSTS)
            ARISING FROM OR RELATING TO: (A) YOUR VIOLATION OF ANY FEDERAL, STATE, OR LOCAL LAWS, STATUTES, RULES, OR
            REGULATIONS; or (B) YOUR FALSE STATEMENTS, MISREPRESENTATIONS, FRAUD, OR NEGLIGENCE.
          </p>
          <p>
            5.5. IN NO EVENT WILL VISALEX BE LIABLE TO YOU FOR ANY AMOUNT IN EXCESS OF THE AMOUNT ACTUALLY PAID BY YOU
            TO VISALEX PURSUANT TO THIS AGREEMENT, FOR THE SUPPORT SERVICES GIVING RISE TO YOUR CLAIM. FOR THE AVOIDANCE
            OF DOUBT, YOU AGREE THAT VISALEX, TOGETHER WITH THE VISALEX PARTIES, SHALL NOT BE LIABLE TO YOU FOR ANY
            INDIRECT, PUNITIVE, SPECIAL, INCIDENTAL, OR CONSEQUENTIAL DAMAGES ARISING OUT OF OR RELATING TO THE SUPPORT
            SERVICES OR THIS AGREEMENT.
          </p>
          <p>
            5.6. YOU UNDERSTAND AND AGREE THAT THE FOREGOING DISCLAIMERS, WAIVERS, AND LIMITATIONS OF LIABILITY ARE
            MATERIAL INDUCEMENTS FOR VISALEX’S ACCEPTANCE OF THIS AGREEMENT, AND ITS FURNISHING OF SUPPORT SERVICES TO
            YOU PURSUANT TO THIS AGREEMENT.
          </p>

          <h2>6. COMPLETE AGREEMENT, PUBLICITY, NO AGENCY OR THIRD-PARTY BENEFICIARIES</h2>
          <p>
            6.1. This Agreement, together with each price quotation issued pursuant to this Agreement, and the VISALEX
            Privacy Policy at visalex.com/privacy-policy, all of which are incorporated herein by reference, comprise
            the complete agreement of the Parties with respect to the subject matter therein. No modification of this
            Agreement will be valid unless made in writing and signed by both parties, except that VISALEX may modify
            the VISALEX Privacy Policy, provided such modifications will apply prospectively only.
          </p>
          <p>6.2. You grant VISALEX the right to add Your name and company logo to its customer lists and website.</p>
          <p>
            6.3. The parties hereto are engaged as independent contractors, and this Agreement does not create any
            agency, partnership, or joint venture relationship between the parties. This Agreement does not and is not
            intended to confer any rights or remedies on any person or entity other than the parties hereto.
          </p>

          <h2>7. ANTI-BRIBERY AND TRADE SANCTIONS</h2>
          <p>
            7.1. VISALEX represents and warrants to You that neither VISALEX nor any of its officers, directors,
            employees, agents, or other representatives has engaged or will engage in any form of commercial bribery or
            otherwise offer any incentive to be made directly or indirectly to any of Your employees or associates of
            such in exchange for or otherwise in an attempt to procure or retain business for VISALEX. VISALEX shall
            comply with the U.S. Foreign Corrupt Practices Act, the UK Bribery Act and all other local or otherwise
            applicable laws dealing with the bribery of government officials or employees.
          </p>
          <p>
            7.2. You represent and warrant to VISALEX that none of your Matters pertain to entities or individuals who
            are (a) designated on any U.S. Government or other governmental list of restricted parties, including the
            List of Specially Designated Nationals and Blocked Persons administered by the Office of Foreign Asset
            Controls at the U.S. Department of the Treasury; (b) located in or otherwise ordinarily resident in any
            country where U.S. or other governmental sanctions or embargo provisions prohibit the provision of the
            Services; or (c) otherwise prohibited from using, benefiting from or accessing the Services.
          </p>

          <h2>8. APPLICABLE LAW</h2>
          <p>
            8.1. Except for the arbitration agreement contained in Section 4, which is governed by the Federal
            Arbitration Act, You and VISALEX each agree that this Agreement is made pursuant to and governed by the
            substantive law of Illinois, without giving effect to its rules for conflicts of laws.
          </p>
        </Col>
      </Container>
    </Layout>
  );
};

export async function getStaticProps({ locale }: { locale: string }) {
  return {
    props: {
      ...(await serverSideTranslations(locale)),
    },
  };
}

export default Terms;
